import React from 'react';
import { Check, CreditCard, ExternalLink, Info } from 'lucide-react';
import {
    CHANNEL_MESSAGE_PRICING,
    PER_CHANNEL_COST_INTRO,
    PER_CHANNEL_COST_NOTE,
    PRICING_EFFECTIVE_DATE,
} from '../../../utils/channelMessagePricing';

// Marketing copy, not enforcement. Keep these cards limited to the approved
// package commitments; feature availability is enforced by the owning services.
const PLANS = [
    {
        id: 'starter',
        name: 'Starter',
        price: '$15',
        detail: '5 surveys/month',
        points: [
            'Up to 20 recipients per survey',
            '2 AI engines',
            'Email support',
            'CSV data export',
        ],
    },
    {
        id: 'professional',
        name: 'Professional',
        price: '$50',
        detail: '100 sessions/month',
        points: [
            'Up to 500 recipients',
            'Advanced analytics & visualizations',
            '4 AI engines, BYO LLM',
            'PDF export',
            'Custom branding',
            'API access',
        ],
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        price: '$100',
        detail: '500 sessions/month',
        points: [
            'Up to 1,000 recipients',
            '6 AI engines, BYO LLM',
            'Advanced integrations',
            'Custom contracts',
        ],
    },
];

export function PlanSelector({ currentPlan, accessStatus, onSelectPlan, loading, checkoutPlan }) {
    const isPaid = accessStatus === 'active_paid';

    function getButtonState(planId) {
        const isCurrent = isPaid && currentPlan === planId;
        const isBusy = checkoutPlan === planId;

        if (isCurrent) return { text: 'Current plan', disabled: true, style: 'current' };
        if (isBusy) return { text: 'Starting...', disabled: true, style: 'busy' };
        // Checkout creates a new subscription. Using it for an already-paid
        // account would double-bill that organization. Plan changes stay
        // disabled until a single-subscription change flow is implemented.
        if (isPaid) return { text: 'Manage billing above', disabled: true, style: 'secondary' };
        return { text: 'Start plan', disabled: false, style: 'primary' };
    }

    return (
        <section style={styles.section}>
            <h3 style={styles.sectionTitle}>Plans</h3>
            <div style={styles.planGrid}>
                {PLANS.map(plan => {
                    const button = getButtonState(plan.id);
                    return (
                        <article key={plan.id} style={styles.planCard}>
                            <div style={styles.planTop}>
                                <div>
                                    <div style={styles.planName}>{plan.name}</div>
                                    <div style={styles.planDetail}>{plan.detail}</div>
                                </div>
                                <div style={styles.price}>
                                    {plan.price}<span style={styles.pricePeriod}>/month</span>
                                </div>
                            </div>
                            <div style={styles.planPoints}>
                                {plan.points.map(point => (
                                    <div key={point} style={styles.point}>
                                        <Check size={14} />
                                        <span>{point}</span>
                                    </div>
                                ))}
                                {/* The plan price does not include messages on
                                    channels that bill per message. Say so on
                                    every card, not once in the fine print. */}
                                <div style={styles.planChannelNote}>
                                    {PER_CHANNEL_COST_NOTE}
                                </div>
                            </div>
                            <button
                                type="button"
                                disabled={button.disabled || loading}
                                onClick={() => onSelectPlan(plan.id)}
                                style={{
                                    ...styles.button,
                                    ...(button.style === 'current' ? styles.buttonCurrent : {}),
                                    ...(button.style === 'busy' ? styles.buttonBusy : {}),
                                    ...(button.style === 'secondary' ? styles.buttonSecondary : {}),
                                }}
                            >
                                <CreditCard size={16} />
                                <span>{button.text}</span>
                                {button.style !== 'current' && <ExternalLink size={14} />}
                            </button>
                        </article>
                    );
                })}
            </div>

            <ChannelCostDetails />
        </section>
    );
}

// The detailed answer to the note on each plan card. It states the customer
// price only. The split between Meta, the carrier and SamSurveyor is billing
// detail and is deliberately left out.
function ChannelCostDetails() {
    return (
        <div style={styles.channelBlock}>
            <div style={styles.channelHeader}>
                <Info size={15} />
                <h4 style={styles.channelTitle}>Per-channel message costs</h4>
            </div>
            <p style={styles.channelIntro}>{PER_CHANNEL_COST_INTRO}</p>

            {CHANNEL_MESSAGE_PRICING.map(channel => (
                <div key={channel.id} style={styles.channelRow}>
                    <div style={styles.channelNameLine}>
                        <span style={styles.channelName}>{channel.name}</span>
                        <span
                            style={{
                                ...styles.channelBadge,
                                ...(channel.status === 'metered' ? styles.badgeMetered : {}),
                                ...(channel.status === 'pending' ? styles.badgePending : {}),
                            }}
                        >
                            {channel.status === 'metered' && 'Charged per message'}
                            {channel.status === 'included' && 'Included in your plan'}
                            {channel.status === 'pending' && 'Not open yet'}
                        </span>
                    </div>
                    <p style={styles.channelSummary}>{channel.summary}</p>

                    {channel.rates.length > 0 && (
                        <div style={styles.rateList}>
                            {channel.rates.map(rate => (
                                <div key={rate.event} style={styles.rateRow}>
                                    <div style={styles.rateEvent}>
                                        <div>{rate.event}</div>
                                        {rate.note && <div style={styles.rateNote}>{rate.note}</div>}
                                    </div>
                                    <div style={styles.ratePrice}>{rate.price}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {channel.example && (
                        <p style={styles.channelExample}>{channel.example}</p>
                    )}
                </div>
            ))}

            <p style={styles.channelFooter}>
                Rates effective {PRICING_EFFECTIVE_DATE}. You are charged for
                messages that were actually delivered. Before you send, the
                Console shows an estimate for the survey and the recipients you
                selected.
            </p>
        </div>
    );
}

const styles = {
    section: {
        marginBottom: 24,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: '#CBD5E1',
        margin: '0 0 14px',
    },
    planGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 14,
    },
    planCard: {
        border: '1px solid rgba(148, 163, 184, 0.18)',
        borderRadius: 8,
        background: '#0F172A',
        padding: 16,
        minHeight: 240,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    planTop: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        alignItems: 'flex-start',
    },
    planName: {
        fontSize: 16,
        fontWeight: 700,
        color: '#F8FAFC',
    },
    planDetail: {
        fontSize: 12,
        lineHeight: 1.45,
        color: '#C3CDDC',
        marginTop: 4,
    },
    price: {
        fontSize: 22,
        fontWeight: 800,
        color: '#F8FAFC',
        whiteSpace: 'nowrap',
    },
    pricePeriod: {
        fontSize: 12,
        fontWeight: 600,
        color: '#C3CDDC',
    },
    planPoints: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        margin: '18px 0',
    },
    point: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        color: '#CBD5E1',
        fontSize: 12,
        lineHeight: 1.35,
    },
    button: {
        width: '100%',
        minHeight: 38,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        border: '1px solid rgba(96, 165, 250, 0.4)',
        borderRadius: 6,
        background: '#2563EB',
        color: '#FFFFFF',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
    },
    buttonCurrent: {
        background: 'rgba(34, 197, 94, 0.12)',
        borderColor: 'rgba(34, 197, 94, 0.3)',
        color: '#86EFAC',
        cursor: 'default',
    },
    buttonBusy: {
        opacity: 0.75,
        cursor: 'wait',
    },
    buttonSecondary: {
        background: 'transparent',
        borderColor: 'rgba(148, 163, 184, 0.25)',
        color: '#94A3B8',
    },
    planChannelNote: {
        marginTop: 4,
        paddingTop: 8,
        borderTop: '1px solid rgba(148, 163, 184, 0.14)',
        color: '#C3CDDC',
        fontSize: 11,
        lineHeight: 1.35,
        fontStyle: 'italic',
    },
    channelBlock: {
        marginTop: 18,
        border: '1px solid rgba(148, 163, 184, 0.18)',
        borderRadius: 8,
        background: '#0F172A',
        padding: 16,
    },
    channelHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: '#CBD5E1',
    },
    channelTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: '#E2E8F0',
        margin: 0,
    },
    channelIntro: {
        fontSize: 12,
        lineHeight: 1.6,
        color: '#C3CDDC',
        margin: '10px 0 16px',
    },
    channelRow: {
        paddingTop: 12,
        marginTop: 12,
        borderTop: '1px solid rgba(148, 163, 184, 0.12)',
    },
    channelNameLine: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
    },
    channelName: {
        fontSize: 13,
        fontWeight: 700,
        color: '#F1F5F9',
    },
    channelBadge: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.02em',
        padding: '2px 8px',
        borderRadius: 999,
        color: '#86EFAC',
        background: 'rgba(34, 197, 94, 0.12)',
        border: '1px solid rgba(34, 197, 94, 0.28)',
    },
    badgeMetered: {
        color: '#FCD34D',
        background: 'rgba(234, 179, 8, 0.12)',
        borderColor: 'rgba(234, 179, 8, 0.3)',
    },
    badgePending: {
        color: '#94A3B8',
        background: 'rgba(148, 163, 184, 0.1)',
        borderColor: 'rgba(148, 163, 184, 0.24)',
    },
    channelSummary: {
        fontSize: 12,
        lineHeight: 1.55,
        color: '#C3CDDC',
        margin: '6px 0 0',
    },
    rateList: {
        marginTop: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    rateRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        padding: '6px 10px',
        borderRadius: 6,
        background: 'rgba(148, 163, 184, 0.06)',
    },
    rateEvent: {
        fontSize: 12,
        lineHeight: 1.45,
        color: '#CBD5E1',
    },
    rateNote: {
        fontSize: 11,
        color: '#C3CDDC',
        marginTop: 2,
    },
    ratePrice: {
        fontSize: 13,
        fontWeight: 700,
        color: '#F8FAFC',
        whiteSpace: 'nowrap',
    },
    channelExample: {
        fontSize: 12,
        lineHeight: 1.55,
        color: '#C3CDDC',
        margin: '10px 0 0',
    },
    channelFooter: {
        fontSize: 11,
        lineHeight: 1.55,
        color: '#B4C0D3',
        margin: '16px 0 0',
        paddingTop: 12,
        borderTop: '1px solid rgba(148, 163, 184, 0.12)',
    },
};
